/**
 * Bounce animation utility functions for AnimatedItem interface
 * Provides bouncing entrance animations with elastic behavior
 */

import { interpolate, Easing } from 'remotion';

/**
 * Calculate bounce animation transform values using elastic easing
 * @param progress - Animation progress (0 to 1)
 * @param direction - Animation direction ('entrance' or 'exit')
 * @param bounces - Number of bounces (default: 3)
 * @param amplitude - Bounce amplitude (default: 0.3)
 * @returns Transform values for current animation frame
 */
export function calculateBounceTransform(
  progress: number,
  direction: 'entrance' | 'exit',
  bounces: number = 3,
  amplitude: number = 0.3
): { scale: number; opacity: number } {
  // Validate inputs
  if (progress < 0 || progress > 1) {
    console.warn(`Invalid progress value: ${progress}. Must be between 0 and 1.`);
    progress = Math.max(0, Math.min(1, progress));
  }
  
  if (amplitude < 0 || amplitude > 1) {
    console.warn(`Invalid amplitude value: ${amplitude}. Should be between 0 and 1.`);
    amplitude = Math.max(0, Math.min(1, amplitude));
  }

  // Use Remotion's interpolate for smooth progress transition
  const easedProgress = direction === 'entrance' 
    ? interpolate(progress, [0, 1], [0, 1], {
        easing: Easing.out(Easing.cubic)
      })
    : interpolate(progress, [0, 1], [1, 0], {
        easing: Easing.in(Easing.cubic)
      });
  
  let scale: number;
  
  if (easedProgress === 0) {
    scale = 0;
  } else if (easedProgress === 1) {
    scale = 1;
  } else {
    // Calculate elastic bounce effect using bounces parameter
    const c4 = (2 * Math.PI) / (bounces || 3);
    const bounceScale = Math.pow(2, -10 * easedProgress) * Math.sin((easedProgress * bounces * 2 - 0.75) * c4) + 1;
    
    // Apply amplitude and ensure minimum scale
    scale = Math.max(0.1, bounceScale * (1 + amplitude));
    
    // Clamp to reasonable values
    scale = Math.min(1.2, scale);
  }
  
  // Fade in during bounce for smoother appearance
  const opacity = Math.min(1, easedProgress * 1.2);
  
  return { scale, opacity };
}

/**
 * Get CSS transform for bounce animation with hardware acceleration
 * @param scale - Current scale value
 * @param opacity - Current opacity value
 * @returns CSS style object for bounce animation
 */
export function getBounceAnimationStyle(scale: number, opacity: number): React.CSSProperties {
  return {
    transform: `scale3d(${scale}, ${scale}, 1)`,
    opacity,
    willChange: 'transform, opacity',
    transformOrigin: 'center center',
  };
}

/**
 * Calculate bounce animation with default settings
 */
export function calculateBounceAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  const { scale, opacity } = calculateBounceTransform(progress, direction);
  return getBounceAnimationStyle(scale, opacity);
}